var frameThrottle = require("enyo-perf/utils").frameThrottle,
    kind          = require("enyo/kind");

require("enyo-ui");

module.exports = {

    /**
     * @private
     */
    name: "enyo-ui.ResizeSensorSupport",

    /**
     * @private
     */
    resizeSensor: null,

    /**
     * Evaluate acceleration support and accelerate control as needed.
     *
     * @memberof enyo-ui.ResizeSensorSupport
     * @method
     * @private
     */
    create: kind.inherit(function(sup) {
        return function() {
            this.onResizeSensorScrolled = this.bindSafely(this.onResizeSensorScrolled);
            this.onResizeSensorResized  = frameThrottle(this.bindSafely(this.onResizeSensorResized));
            this.createResizeSensor();
            sup.apply(this, arguments);
        };
    }),

    destroy: kind.inherit(function(sup) {
        return function() {
            this.resizeSensor && this.destroyResizeSensor();
            sup.apply(this, arguments);
        };
    }),

    /**
     * Destroy the resize sensor before tearing down the control.
     *
     * @memberof enyo-ui.ResizeSensorSupport
     * @method
     * @private
     */
    teardownRender: kind.inherit(function(sup) {
        return function() {
            this.detachResizeSensor();
            sup.apply(this, arguments);
        };
    }),

    /**
     * Create the resizeSensor as soon as the control has been rendered.
     *
     * @memberof enyo-ui.ResizeSensorSupport
     * @method
     * @private
     */
    rendered: kind.inherit(function(sup) {
        return function() {
            this.attachResizeSensor();
            sup.apply(this, arguments);
        };
    }),

    /**
     * @private
     */
    absoluteShowingChanged: kind.inherit(function(sup) {
        return function(wasShowing, isShowing) {
            if(wasShowing
                && !isShowing
                && this.resizeSensor) {
                this.resizeSensor.width = 0;
                this.resizeSensor.height = 0;
            }

            else if(!wasShowing
                && isShowing
                && !this.needsReflow
                && this.resizeSensor) {
                this.calibrateResizeSensor();
            };
            sup.apply(this, arguments);
        };
    }),

    /**
     * Recalibrate the resizeSensor after reflow.
     *
     * @memberof enyo-ui.ResizeSensorSupport
     * @method
     * @private
     */
    reflow: kind.inherit(function(sup) {
        return function() {
            sup.apply(this, arguments);
            this.resizeSensor && this.calibrateResizeSensor();
        };
    }),

    /**
     * Creates resize sensor nodes and initializes resizeSensor state.
     *
     * @memberof enyo-ui.ResizeSensorSupport
     * @private
     */
    createResizeSensor: function() {
        var expand,
            expandChild,
            shrink,
            shrinkChild;

        expand                  = document.createElement("div");
        expand.className        = "expand-sensor";
        expand.style.position   = "absolute";
        expand.style.left       = "0";
        expand.style.top        = "0";
        expand.style.right      = "0";
        expand.style.bottom     = "0";
        expand.style.overflow   = "hidden";
        expand.style.visibility = "hidden";

        expandChild                = document.createElement("div");
        expandChild.style.position = "absolute";
        expandChild.style.left     = "0";
        expandChild.style.top      = "0";
        expandChild.style.width    = "10000000px";
        expandChild.style.height   = "10000000px";
        expand.appendChild(expandChild);

        shrink                  = document.createElement("div");
        shrink.className        = "shrink-sensor";
        shrink.style.position   = "absolute";
        shrink.style.left       = "0";
        shrink.style.top        = "0";
        shrink.style.right      = "0";
        shrink.style.bottom     = "0";
        shrink.style.overflow   = "hidden";
        shrink.style.visibility = "hidden";

        shrinkChild                = document.createElement("div");
        shrinkChild.style.position = "absolute";
        shrinkChild.style.left     = "0";
        shrinkChild.style.top      = "0";
        shrinkChild.style.width    = "200%";
        shrinkChild.style.height   = "200%";
        shrink.appendChild(shrinkChild);

        this.resizeSensor = {
            width:  undefined,
            height: undefined,
            expand: expand,
            shrink: shrink
        };
    },

    /**
     * Attach the resize sensor to the DOM and start sensing.
     *
     * @private
     */
    attachResizeSensor: function() {
        var resizeSensor = this.resizeSensor,
            expand       = resizeSensor.expand,
            shrink       = resizeSensor.shrink,
            node         = this.hasNode(),
            zIndex       = this.getComputedStyleValue("z-index", 0),
            position;

        zIndex = isNaN(zIndex) ? -1 : zIndex--;

        expand.style.zIndex = zIndex;
        shrink.style.zIndex = zIndex;

        node.appendChild(expand);
        node.appendChild(shrink);

        position = this.getComputedStyleValue("position", null);

        if ("absolute" !== position && "relative" !== position && "fixed" !== position && "sticky" !== position) {
            this.applyStyle(position, "relative");
        }

        expand.addEventListener("scroll", this.onResizeSensorScrolled);
        shrink.addEventListener("scroll", this.onResizeSensorScrolled);
    },

    /**
     * Stop sensing and detach the resize sensor from the DOM.
     *
     * @private
     */
    detachResizeSensor: function() {
        var resizeSensor = this.resizeSensor,
            expand       = resizeSensor && resizeSensor.expand,
            shrink       = resizeSensor && resizeSensor.shrink;

        if (expand) {
            expand.removeEventListener("scroll", this.onResizeSensorScrolled);
            expand.remove();
        }

        if (shrink) {
            shrink.removeEventListener("scroll", this.onResizeSensorScrolled);
            shrink.remove();
        }
    },

    /**
     * Detaches resizeSensor and deletes it.
     *
     * @memberof enyo-ui.ResizeSensorSupport
     * @private
     */
    destroyResizeSensor: function() {
        this.detachResizeSensor();
        this.resizeSensor = null;
    },

    /**
     * Resets the resize sensor so it can detect the next size change.
     *
     * @memberof enyo-ui.ResizeSensorSupport
     * @private
     */
    calibrateResizeSensor: function() {
        var resizeSensor = this.resizeSensor,
            expand       = resizeSensor.expand,
            shrink       = resizeSensor.shrink;

        if (expand) {
            expand.scrollLeft = 10000000;
            expand.scrollTop  = 10000000;
        }

        if (shrink) {
            shrink.scrollLeft = 10000000;
            shrink.scrollTop  = 10000000;
        }
    },

    /**
     * React to possible size change and schedule a callback to broadcast the
     * change at most once per animation frame.
     *
     * @memberof enyo-ui.ResizeSensorSupport
     * @private
     */
    onResizeSensorScrolled: function() {
        // Call the throttled resize handler.
        this.onResizeSensorResized();

        // Sensor can be calibrated synchronously
        this.calibrateResizeSensor();
    },

    /**
     * React to size change. This method will be throttled to fire at most once
     * per animationFrame.
     *
     * @memberof enyo-ui.ResizeSensorSupport
     * @private
     */
    onResizeSensorResized: function() {
        var resizeSensor = this.resizeSensor,
            node         = this.hasNode(),
            size         = node.getBoundingClientRect();

        // Only send an event if size actually changed.
        if (size.width !== resizeSensor.width
            || size.height !== resizeSensor.height)  {

            // Memorize old size for next diff
            resizeSensor.width  = size.width;
            resizeSensor.height = size.height;

            // Let the world know
            this.bubble("onNodeResized", {
                width:  size.width,
                height: size.height
            }, this);
        }

    }

};
